"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import {
  Shield, Zap, Lock, EyeOff, XCircle, Smartphone, Globe,
  Server, Wifi, Key, Eye, CheckCircle, ArrowRight,
  Cpu, HardDrive, RefreshCw, GitBranch, Cloud, Download,
  Monitor, Router, Users, MapPin, Clock, Layers,
} from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Shield, Zap, Lock, EyeOff, XCircle, Smartphone, Globe,
  Server, Wifi, Key, Eye, CheckCircle, ArrowRight,
  Cpu, HardDrive, RefreshCw, GitBranch, Cloud, Download,
  Monitor, Router, Users, MapPin, Clock, Layers,
  shield: Shield, zap: Zap, lock: Lock, "eye-off": EyeOff,
  "x-circle": XCircle, smartphone: Smartphone, globe: Globe,
  server: Server, wifi: Wifi, key: Key, eye: Eye,
  "check-circle": CheckCircle, "arrow-right": ArrowRight,
  cpu: Cpu, "hard-drive": HardDrive, "refresh-cw": RefreshCw,
  "git-branch": GitBranch, cloud: Cloud, download: Download,
  monitor: Monitor, router: Router, users: Users,
  "map-pin": MapPin, clock: Clock, layers: Layers,
};

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface IconFeatureListProps {
  title: string;
  subtitle?: string;
  features: Feature[];
}

const IconFeatureList = ({ title, subtitle, features }: IconFeatureListProps) => {
  if (!features || features.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-4xl mx-auto flex flex-col items-center gap-16">
        {/* Header */}
        <div className="text-center space-y-4">
          <BlurFade delay={0.1}>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              {title}
            </h2>
          </BlurFade>
          {subtitle && (
            <BlurFade delay={0.15}>
              <p className="text-foreground/60 text-lg max-w-2xl mx-auto">
                {subtitle}
              </p>
            </BlurFade>
          )}
        </div>

        {/* Feature List */}
        <div className="w-full space-y-6">
          {features.map((feature, i) => {
            const Icon = ICON_MAP[feature.icon] || Shield;
            return (
              <BlurFade key={i} delay={0.2 + i * 0.06}>
                <div className="flex items-start gap-6 p-6 rounded-3xl bg-muted/50 hover:bg-muted transition-colors">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-sm border border-foreground/5">
                    <Icon size={24} className="text-foreground/70" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <h3 className="text-lg font-bold text-foreground">
                      {feature.title}
                    </h3>
                    <p className="text-foreground/60 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </BlurFade>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default IconFeatureList;
